import React from 'react';
import { useTranslation } from "react-i18next";
import TableHeader from './TableHeader';
import Pagination from './Pagination';
import { TableSkeleton } from './Skeleton';
import { usePagination } from '../../../hooks/usePagination';
import { useTableSearch } from '../../../hooks/useTableSearch';
import './Table.css';
import './EmptyState.css';

/**
 * DataTable Component
 * Reusable table with search, pagination, sorting, and customizable content
 * 
 * Follows principles:
 * - Component-based architecture
 * - Composition over inheritance
 * - Unidirectional data flow
 * - Separation of concerns
 */
export default function DataTable({
  // Data
  data = [],
  
  // Table configuration
  columns = [],
  renderRow,
  
  // Search configuration
  searchFields = [],
  searchPlaceholder,
  showSearch = true,
  
  // Header configuration
  title,
  subtitle,
  showHeader = true,
  
  // Filter configuration (optional)
  filterValue,
  onFilterChange,
  filterOptions = [],
  filterLabel = 'Filter',
  showFilter = false,
  
  // Sorting configuration
  defaultSortKey = null,
  defaultSortDirection = 'asc',
  
  // Pagination configuration
  initialItemsPerPage = 10,
  itemsPerPageOptions = [5, 10, 25, 50],
  itemsPerPagePlacement = "bottom", // "bottom" | "top"
  
  // Table class customization
  tableClassName = "admin-users-table",
  containerClassName = "admin-users-section",
  
  // Empty state
  emptyMessage,
  
  // Loading & Error states
  loading = false,
  error = null,

  // Server mode configuration (optional)
  serverMode = false,
  serverPagination = null, // { total, limit, offset }
  onServerPaginationChange = null, // ({ limit, offset }) => void
  serverSort = null, // { key, direction }
  onServerSortChange = null, // ({ key, direction }) => void
  externalSearchQuery = "",
  onExternalSearchChange = null,
}) {
  const { t } = useTranslation();
  const getPageNumbers = React.useCallback((current, total) => {
    const pages = [];

    if (total <= 7) {
      for (let i = 1; i <= total; i += 1) pages.push(i);
      return pages;
    }

    pages.push(1);
    let startPage = Math.max(2, current - 2);
    let endPage = Math.min(total - 1, current + 2);

    if (current <= 4) {
      startPage = 2;
      endPage = 5;
    } else if (current >= total - 3) {
      startPage = total - 4;
      endPage = total - 1;
    }

    if (startPage > 2) pages.push("...");
    for (let i = startPage; i <= endPage; i += 1) pages.push(i);
    if (endPage < total - 1) pages.push("...");
    pages.push(total);

    return pages;
  }, []);

  // Ref for scroll position preservation
  const tableWrapperRef = React.useRef(null);
  const scrollPositionRef = React.useRef(0);

  // Pagination loading state
  const [isPaginationLoading, setIsPaginationLoading] = React.useState(false);

  // Sorting state - initialize with default sort if provided
  const [sortConfig, setSortConfig] = React.useState({
    key: serverSort?.key || defaultSortKey,
    direction: serverSort?.direction || defaultSortDirection
  });

  // Use custom hooks for search and pagination
  const { searchQuery, filteredData, setSearchQuery } = useTableSearch(
    data,
    searchFields
  );

  const dataForSort = React.useMemo(
    () => (serverMode ? data : filteredData),
    [serverMode, data, filteredData],
  );

  // Sort data
  const sortedData = React.useMemo(() => {
    if (serverMode && typeof onServerSortChange === "function") {
      return dataForSort;
    }

    if (!sortConfig.key) return dataForSort;

    const sorted = [...dataForSort].sort((a, b) => {
      const column = columns.find(col => col.sortKey === sortConfig.key);
      const aValue = column?.sortValue ? column.sortValue(a) : a[sortConfig.key];
      const bValue = column?.sortValue ? column.sortValue(b) : b[sortConfig.key];

      if (aValue === bValue) return 0;
      
      const comparison = aValue < bValue ? -1 : 1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [serverMode, onServerSortChange, dataForSort, sortConfig, columns]);

  React.useEffect(() => {
    if (!serverMode || typeof onServerSortChange !== "function") return;
    if (!serverSort) return;

    setSortConfig((previous) => {
      const nextKey = serverSort.key || null;
      const nextDirection = serverSort.direction || null;
      if (previous.key === nextKey && previous.direction === nextDirection) {
        return previous;
      }
      return {
        key: nextKey,
        direction: nextDirection,
      };
    });
  }, [serverMode, onServerSortChange, serverSort]);

  const {
    currentPage: internalCurrentPage,
    itemsPerPage: internalItemsPerPage,
    totalPages: internalTotalPages,
    paginatedData: internalPaginatedData,
    pageNumbers: internalPageNumbers,
    goToPage: goToInternalPage,
    setItemsPerPage: setInternalItemsPerPage,
    resetToFirstPage,
  } = usePagination(sortedData, initialItemsPerPage);

  const resolvedServerPagination = React.useMemo(() => {
    const fallbackLimit = Number.parseInt(initialItemsPerPage, 10) || 10;
    const requestedLimit = Number.parseInt(serverPagination?.limit, 10);
    const requestedOffset = Number.parseInt(serverPagination?.offset, 10);
    const requestedTotal = Number.parseInt(serverPagination?.total, 10);

    return {
      limit: Number.isInteger(requestedLimit) && requestedLimit > 0 ? requestedLimit : fallbackLimit,
      offset: Number.isInteger(requestedOffset) && requestedOffset >= 0 ? requestedOffset : 0,
      total: Number.isInteger(requestedTotal) && requestedTotal >= 0 ? requestedTotal : data.length,
    };
  }, [serverPagination, initialItemsPerPage, data.length]);

  const effectiveItemsPerPage = serverMode
    ? resolvedServerPagination.limit
    : internalItemsPerPage;
  const effectiveCurrentPage = serverMode
    ? Math.floor(resolvedServerPagination.offset / resolvedServerPagination.limit) + 1
    : internalCurrentPage;
  const effectiveTotalPages = serverMode
    ? Math.max(1, Math.ceil(resolvedServerPagination.total / resolvedServerPagination.limit))
    : internalTotalPages;
  const effectivePageNumbers = serverMode
    ? getPageNumbers(effectiveCurrentPage, effectiveTotalPages)
    : internalPageNumbers;
  const effectiveData = serverMode ? sortedData : internalPaginatedData;
  const effectiveTotalItems = serverMode ? resolvedServerPagination.total : filteredData.length;

  // Save scroll position before data updates
  React.useEffect(() => {
    if (tableWrapperRef.current && !loading && !isPaginationLoading) {
      scrollPositionRef.current = tableWrapperRef.current.scrollTop;
    }
  }, [data, loading, isPaginationLoading]);

  // Restore scroll position after data updates (CRUD operations)
  React.useEffect(() => {
    if (tableWrapperRef.current && !loading && !isPaginationLoading && scrollPositionRef.current > 0) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (tableWrapperRef.current) {
          tableWrapperRef.current.scrollTop = scrollPositionRef.current;
        }
      });
    }
  }, [effectiveData, loading, isPaginationLoading]);

  React.useEffect(() => {
    if (!loading) {
      setIsPaginationLoading(false);
    }
  }, [loading]);

  // Wrapped pagination handlers with loading state
  const handlePageChange = React.useCallback((page) => {
    scrollPositionRef.current = 0; // Reset scroll for page changes
    setIsPaginationLoading(true);

    if (serverMode) {
      if (typeof onServerPaginationChange === "function") {
        const normalizedPage = Math.max(1, Math.min(page, effectiveTotalPages));
        onServerPaginationChange({
          limit: resolvedServerPagination.limit,
          offset: (normalizedPage - 1) * resolvedServerPagination.limit,
        });
      } else {
        setIsPaginationLoading(false);
      }
      return;
    }

    goToInternalPage(page);
    setTimeout(() => setIsPaginationLoading(false), 200);
  }, [
    serverMode,
    onServerPaginationChange,
    effectiveTotalPages,
    resolvedServerPagination.limit,
    goToInternalPage,
  ]);

  const handleNextPage = React.useCallback(() => {
    handlePageChange(effectiveCurrentPage + 1);
  }, [handlePageChange, effectiveCurrentPage]);

  const handlePreviousPage = React.useCallback(() => {
    handlePageChange(effectiveCurrentPage - 1);
  }, [handlePageChange, effectiveCurrentPage]);

  const handleItemsPerPageChange = React.useCallback((newItemsPerPage) => {
    if (serverMode) {
      if (typeof onServerPaginationChange === "function") {
        const normalizedLimit = Math.max(1, Number.parseInt(newItemsPerPage, 10) || initialItemsPerPage);
        setIsPaginationLoading(true);
        onServerPaginationChange({
          limit: normalizedLimit,
          offset: 0,
        });
      }
      return;
    }

    setInternalItemsPerPage(newItemsPerPage);
  }, [serverMode, onServerPaginationChange, initialItemsPerPage, setInternalItemsPerPage]);

  // Reset to first page when search changes
  React.useEffect(() => {
    if (!serverMode) {
      resetToFirstPage();
    }
  }, [searchQuery, resetToFirstPage, serverMode]);

  // Handle search change
  const handleSearchChange = (query) => {
    if (serverMode && typeof onExternalSearchChange === "function") {
      onExternalSearchChange(query);
      return;
    }
    setSearchQuery(query);
  };

  // Handle sort
  const handleSort = (sortKey) => {
    if (!sortKey) return;

    const getNextSortConfig = (previousConfig) => {
      if (previousConfig.key === sortKey) {
        if (previousConfig.direction === "asc") {
          return { key: sortKey, direction: "desc" };
        }
        if (previousConfig.direction === "desc") {
          return { key: null, direction: null };
        }
      }
      return { key: sortKey, direction: "asc" };
    };

    if (serverMode && typeof onServerSortChange === "function") {
      setSortConfig((previous) => {
        const next = getNextSortConfig(previous);
        onServerSortChange(next);
        return next;
      });
      return;
    }

    setSortConfig((previous) => getNextSortConfig(previous));
  };

  // Render table header
  const renderTableHeader = () => (
    <thead>
      <tr>
        {columns.map((column, index) => (
          <th 
            key={index}
            style={column.headerStyle}
            className={`
              ${column.headerClassName || ''}
              ${column.sortKey ? 'sortable' : ''}
              ${sortConfig.key === column.sortKey ? `sort-${sortConfig.direction}` : ''}
            `.trim()}
            onClick={() => handleSort(column.sortKey)}
          >
            {column.label}
          </th>
        ))}
      </tr>
    </thead>
  );

  // Error state
  if (error) {
    return <div className="admin-error">{error}</div>;
  }

  return (
    <div className={containerClassName}>
      {showHeader && (
        <TableHeader
          title={title}
          subtitle={
            loading
              ? t("admin.common.loadingSubtitle")
              : subtitle || t("admin.common.itemsCount", { count: effectiveTotalItems })
          }
          searchValue={serverMode ? externalSearchQuery : searchQuery}
          onSearchChange={handleSearchChange}
          searchPlaceholder={searchPlaceholder || t("admin.common.search")}
          showSearch={showSearch}
          itemsPerPage={effectiveItemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
          itemsPerPageOptions={itemsPerPageOptions}
          itemsPerPagePlacement={itemsPerPagePlacement}
          filterValue={filterValue}
          onFilterChange={onFilterChange}
          filterOptions={filterOptions}
          filterLabel={filterLabel}
          showFilter={showFilter}
          loading={loading}
        />
      )}

      {/* PROPER STATE MACHINE: Only content swaps */}
      {loading || isPaginationLoading ? (
        <TableSkeleton
          columns={columns}
          rowCount={effectiveItemsPerPage}
          tableClassName={tableClassName}
          loading={loading || isPaginationLoading}
        />
      ) : effectiveData.length === 0 ? (
        // EMPTY: Show empty state (no skeleton)
        <div className="admin-no-data">{emptyMessage || t("admin.common.noDataFound")}</div>
      ) : (
        // LOADED: Show actual data + pagination
        <>
          <div className="admin-table-wrapper" ref={tableWrapperRef}>
            <table className={tableClassName}>
              {renderTableHeader()}
              <tbody>
                {effectiveData.map((item, index) => renderRow(item, index))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={effectiveCurrentPage}
            totalPages={effectiveTotalPages}
            pageNumbers={effectivePageNumbers}
            onPageChange={handlePageChange}
            onNext={handleNextPage}
            onPrevious={handlePreviousPage}
          />
        </>
      )}
    </div>
  );
}
